import {
  ColumnDescriptor,
  ColumnType,
  SourceFieldCatalog,
} from '../../models/field-catalog.model'
import { HELIOS_GROUP } from '../../models/source-definition.model'
import { DetailField, RowDetailEntry } from '../../models/row-detail.model'
import {
  GROUP_LABEL_BY_KEY,
  titleCaseGroupToken,
} from '../../grid/field-catalog.adapter'
import {
  formatBoolean,
  formatDate,
  formatDateTime,
  numericValueOf,
} from '../../grid/renderers/renderers'

/**
 * Pure group-label resolution, value formatting, empty detection, filtering and sorting for the
 * Position Detail sub-panel. Everything here is a plain function over plain data so it can be
 * unit-tested without a TestBed — the component is left holding only wiring.
 *
 * Reference behaviour is the design prototype's `detailFor()`
 * (`libs/panels/positions-2/docs/Positions 2.0.dc.html:2264-2305`); individual rules cite the line
 * they come from.
 */

/** Placeholder for an absent value — a dimmed em dash reads as "nothing here", not "zero". Matches
 *  the prototype's `detailText` at `:2265` and renderers.ts's own EMPTY. */
export const EMPTY_VALUE = '—'

/**
 * Decimal places for a numeric value.
 *
 * 4dp with NO thousands separators, deliberately. This endpoint returns identifiers
 * (Custodian Account ID, TRS ID, Position Instrument ID) as plain numbers, and a grouped `144,596`
 * would read as an amount; the catalog cannot tell an id NUM field from a quantity NUM field, so
 * the safe rendering is the ungrouped one for both. Rounding to 4dp is also what collapses float
 * noise like `-8.810729923425242e-13` to `0` rather than showing the user scientific notation.
 */
const NUM_DECIMALS = 4

/**
 * Values that mean "nothing" even though they are non-null strings. Enfusion sends the literal
 * string `'null'` for some absent fields (the same quirk the read API's envelope has — see
 * `grid/row.adapter.ts`), which is NOT JSON null and so survives the backend's null-stripping.
 */
const EMPTY_LITERALS: ReadonlySet<string> = new Set(['', 'null', 'undefined'])

const isEmptyLiteral = (text: string): boolean =>
  EMPTY_LITERALS.has(text.trim().toLowerCase())

/** Group keys the backend can emit that are not `ColumnGroupKey` members (a known backend gap,
 *  deferred there — PVAL-26156), each with the label the prototype shows for it. */
const GROUP_LABEL_SPECIAL_CASES: Readonly<Record<string, string | undefined>> =
  {
    aggregateInformation: 'Aggregate Info',
    UNKNOWN: 'Other',
  }

/**
 * The grid's own key->label fallback, widened to a `string` index so an arbitrary backend token
 * looks up as `undefined` rather than needing a cast onto `ColumnGroupKey`.
 */
const SHARED_GROUP_LABELS: Readonly<Record<string, string | undefined>> =
  GROUP_LABEL_BY_KEY

/**
 * Title-case an unrecognised group token. Re-exported from the grid adapter, which the catalog
 * columns go through, so both label ladders resolve an unknown key identically.
 */
export { titleCaseGroupToken }

/** `columnGroup` key -> server-provided display label, from the row's own field catalog. */
export const catalogGroupLabels = (
  catalog: SourceFieldCatalog | null,
): Readonly<Record<string, string | undefined>> => {
  const labels: Record<string, string> = {}
  catalog?.groups.forEach(group => {
    if (group.label) labels[group.key] = group.label
  })
  return labels
}

/**
 * Resolve a raw `columnGroup` key to the label the table shows.
 *
 * Precedence, per the ticket's §6: the row's own catalog (authoritative, and already cached per
 * account+source so this costs nothing), then the shared `GROUP_LABEL_BY_KEY` fallback the grid
 * uses, then the two special cases the backend can emit, then a title-cased token. An unrecognised
 * key never drops the row.
 */
export const resolveGroupLabel = (
  columnGroup: string,
  catalogLabels: Readonly<Record<string, string | undefined>>,
): string =>
  catalogLabels[columnGroup] ??
  SHARED_GROUP_LABELS[columnGroup] ??
  GROUP_LABEL_SPECIAL_CASES[columnGroup] ??
  titleCaseGroupToken(columnGroup)

/** Compare labels ignoring case, spaces and underscores, so `TRS ID` matches `trs_id`. */
const normalizeLabel = (label: string): string =>
  label.replace(/[\s_]+/g, '').toLowerCase()

/**
 * Index the catalog's columns by normalised label, so a `columnName` from the row-detail API can be
 * given a semantic type. The API delivers `columnName` as an already-resolved display label and
 * carries no type at all, so the catalog's `ColumnDescriptor.label` is the only available join.
 *
 * First descriptor wins on a duplicate label, preserving catalog order.
 */
export const columnTypeIndex = (
  columns: readonly ColumnDescriptor[],
): ReadonlyMap<string, ColumnType> => {
  const index = new Map<string, ColumnType>()
  columns.forEach(column => {
    const key = normalizeLabel(column.label)
    if (!index.has(key)) index.set(key, column.type)
  })
  return index
}

/** 4dp, no separators, and `-0` collapsed to `0` (`String(-0)` is already `'0'`, but relying on
 *  that is a coincidence rather than an intention). */
const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return EMPTY_VALUE
  const rounded = +value.toFixed(NUM_DECIMALS)
  return String(rounded === 0 ? 0 : rounded)
}

const formatText = (value: string | number | boolean): string => {
  const text = String(value)
  return isEmptyLiteral(text) ? EMPTY_VALUE : text
}

/** A formatted value, plus whether it was rendered as a number. */
interface FormattedDetailValue {
  text: string
  /** True only when the value went through `formatNumber` and came out finite (not `—`). */
  numeric: boolean
}

const textCell = (text: string): FormattedDetailValue => ({
  text,
  numeric: false,
})

const numberCell = (value: number): FormattedDetailValue => {
  const text = formatNumber(value)
  return { text, numeric: text !== EMPTY_VALUE }
}

/**
 * The single source of truth behind {@link formatDetailValue}: the same ladder, but also reporting
 * whether the result is a number. Kept as one function so the screen text and the Excel export's
 * cell type can never disagree about which values are numbers.
 */
const formatDetailCell = (
  value: string | number | boolean | null | undefined,
  type?: ColumnType,
): FormattedDetailValue => {
  if (value === null || value === undefined) return textCell(EMPTY_VALUE)

  switch (type) {
    case 'DATE':
      return textCell(formatDate(value))
    case 'DATETIME':
      return textCell(formatDateTime(value))
    case 'BOOL':
      return textCell(formatBoolean(value))
    case 'NUM': {
      const numeric = numericValueOf(value)
      return numeric == null ? textCell(formatText(value)) : numberCell(numeric)
    }
    case 'TEXT':
      return textCell(formatText(value))
    default:
      // No catalog match: sniff from the value's own shape. A numeric STRING stays text — only a
      // real JSON number is treated as a number, since an unmatched numeric-looking string is as
      // likely to be an identifier the backend chose to send as a string.
      if (typeof value === 'boolean') return textCell(formatBoolean(value))
      if (typeof value === 'number') return numberCell(value)
      return textCell(formatText(value))
  }
}

/**
 * Format one value for display. The API never formats — `value` is a native JSON scalar — so every
 * presentation rule lives here.
 *
 * `type` comes from the catalog when `columnName` matched a descriptor; when it did not, the shape
 * of the value is sniffed instead. Dates and booleans reuse the grid's own formatters so a field
 * reads identically in the grid above and in this table, which also means this inherits the grid's
 * timezone handling verbatim — the prototype does the same, delegating to the shared `cellText` at
 * `:2265` rather than defining its own date rule.
 *
 * `moneyValueFormatter` is deliberately NOT reused: it returns `—` for any non-Money value, and
 * every value from this endpoint is a bare scalar (Enfusion's `{value, currency}` envelopes are
 * unwrapped server-side), so it would blank the entire table.
 */
export const formatDetailValue = (
  value: string | number | boolean | null | undefined,
  type?: ColumnType,
): string => formatDetailCell(value, type).text

/**
 * Whether a field counts as empty for the "Hide empty fields" toggle.
 *
 * Empty means: null/undefined, `''`, the literal string `'null'`/`'undefined'` (trimmed,
 * case-insensitive), or anything that formatted down to `—`. Note what is NOT empty: `0`, `false`
 * and `'0'` are all real values a user needs to see, and `-8.81e-13` formats to `0` — a genuine
 * (rounded) zero, not an absence.
 */
export const isEmptyDetailValue = (
  value: string | number | boolean | null | undefined,
  formatted: string,
): boolean => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && isEmptyLiteral(value)) return true
  return formatted === EMPTY_VALUE
}

/** Everything derived from one row's response, before the toolbar filters run. */
export interface DetailFieldSet {
  /** Every renderable field, in response order (already `(columnGroup, columnName)`-sorted). */
  fields: DetailField[]
  /** Distinct resolved group labels, in response order — the group dropdown's menu. */
  groups: string[]
  /** The footer's `Total:` — see the comment at its assignment for why it is counted where it is. */
  total: number
}

/**
 * Transpose one row's response into renderable fields.
 *
 * `catalog` is the row's own source catalog, used for group labels and value types; `null` (it
 * failed to load, or no account is focused) is tolerated and falls back to `GROUP_LABEL_BY_KEY` plus
 * value-shape sniffing, because a catalog problem must not blank the detail table.
 */
export const buildDetailFields = (
  entries: readonly RowDetailEntry[],
  catalog: SourceFieldCatalog | null,
): DetailFieldSet => {
  const catalogLabels = catalogGroupLabels(catalog)
  const types = columnTypeIndex(catalog?.columns ?? [])

  const fields: DetailField[] = []
  entries.forEach(entry => {
    const group = resolveGroupLabel(entry.columnGroup, catalogLabels)
    // Helios columns are UI-owned computed columns, not source data, so they are not this row's
    // detail — the prototype filters them out at `:2280`.
    if (group === HELIOS_GROUP) return
    const { text: value, numeric } = formatDetailCell(
      entry.value,
      types.get(normalizeLabel(entry.columnName)),
    )
    fields.push({
      group,
      name: entry.columnName,
      value,
      empty: isEmptyDetailValue(entry.value, value),
      numeric,
    })
  })

  // `Total:` counts every field this row HAS — i.e. after the Helios drop, but BEFORE the group /
  // hide-empty / search filters. The prototype computes it at `:2287` as `all.length`, straight
  // after the Helios filter at `:2280` and before the three filters at `:2288`-`:2290`. Toggling a
  // filter therefore moves `Fields:` and must leave `Total:` alone; counting it any later would
  // make the two numbers say the same thing and lose the "of how many" the footer exists to give.
  const total = fields.length

  return {
    fields,
    groups: [...new Set(fields.map(field => field.group))],
    total,
  }
}

/** The toolbar's three filters. Hidden groups are stored, not shown ones, so a group that appears
 *  in a later response defaults to visible. */
export interface DetailFilterState {
  hiddenGroups: ReadonlySet<string>
  hideEmpty: boolean
  search: string
}

/** Hide empty fields is ON by default, matching the prototype's `detailHideEmpty` default. */
export const defaultDetailFilters = (): DetailFilterState => ({
  hiddenGroups: new Set<string>(),
  hideEmpty: true,
  search: '',
})

/** Case-insensitive substring match across all three rendered columns (prototype `:2290`). */
const matchesDetailSearch = (field: DetailField, query: string): boolean =>
  field.group.toLowerCase().includes(query) ||
  field.name.toLowerCase().includes(query) ||
  field.value.toLowerCase().includes(query)

/**
 * Apply the group filter, hide-empty and search as a single AND — the prototype applies them in
 * that order at `:2288`-`:2290`, and since all three are conjunctive the order is presentational
 * only.
 */
export const filterDetailFields = (
  fields: readonly DetailField[],
  filters: DetailFilterState,
): DetailField[] => {
  const query = filters.search.trim().toLowerCase()
  return fields.filter(
    field =>
      !filters.hiddenGroups.has(field.group) &&
      !(filters.hideEmpty && field.empty) &&
      (query === '' || matchesDetailSearch(field, query)),
  )
}

/**
 * The group dropdown button's text: `All Groups` when everything is on, the group's own name when
 * exactly one is on, otherwise `N Groups`.
 */
export const groupsButtonLabel = (
  groups: readonly string[],
  hiddenGroups: ReadonlySet<string>,
): string => {
  const shown = groups.filter(group => !hiddenGroups.has(group))
  if (shown.length === groups.length) return 'All Groups'
  if (shown.length === 1) return shown[0]
  return `${shown.length} Groups`
}

/** True when every group is currently shown — drives the menu's `Select All` checkbox. */
export const allGroupsShown = (
  groups: readonly string[],
  hiddenGroups: ReadonlySet<string>,
): boolean => groups.every(group => !hiddenGroups.has(group))

/** A value that reads as a number, for the Value column's comparator (prototype `:2295`). */
const numericish = (text: string): number | null => {
  const trimmed = text.trim()
  if (!/^[-(]?[\d,.]+\)?$/.test(trimmed)) return null
  const parsed = parseFloat(trimmed.replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Sort comparator for the table's cells: numeric when BOTH sides read as numbers, otherwise a
 * numeric-aware locale compare so `Field 2` still sorts before `Field 10`.
 */
export const compareDetailCells = (a: string, b: string): number => {
  const an = numericish(a)
  const bn = numericish(b)
  if (an != null && bn != null) return an - bn
  return String(a).localeCompare(String(b), undefined, { numeric: true })
}
